import { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { PasswordResetWrapper, ProceedToLoginWrapper } from "./styled";
import { BaseLegend } from "../../../components/form/legend/styled";
import { BaseButton } from "../../../components/button/styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { AuthLayout } from "../../../containers/layout/auth";
import { resetUserPassword } from "../../../utils/api/authentication/resetPassword";
import { useNavigate } from "react-router-dom";

export const PasswordReset = () => {
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [formDetails, setFormDetails] = useState({
		newPassword: "",
		confirmPassword: "",
	});
	const [isPasswordReset, setIsPasswordReset] = useState(false);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			const response = await resetUserPassword(formDetails);
			if (response.status === "success") {
				setIsLoading(false);
				setIsPasswordReset(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to reset password. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Password reset failed. ${error.message}`);
			console.error("Password reset failed:", error);
		}
	};

	const handleNavigateToLogin = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/");
	};

	return (
		<AuthLayout>
			{isPasswordReset ? (
				<ProceedToLoginWrapper>
					<Box>
						<BaseLegend>Password Successfully Reset!</BaseLegend>
						<Typography
							variant="body1"
							fontFamily={"Inter"}
							fontWeight={400}
							fontSize={"14px"}
							lineHeight={"normal"}
							color="#636364"
							textAlign={"center"}
							whiteSpace={"normal"}
						>
							Password has been successfully reset
						</Typography>
					</Box>
					<Box component={"div"} className="call-to-action">
						<BaseButton
							variant="contained"
							sx={{
								width: "100%",
							}}
							onClick={handleNavigateToLogin}
						>
							<Typography
								variant={"button"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
								textTransform={"inherit"}
							>
								Back to Login
							</Typography>
						</BaseButton>
					</Box>
				</ProceedToLoginWrapper>
			) : (
				<PasswordResetWrapper onSubmit={handleSubmit}>
					<Box>
						<BaseLegend>Reset Password</BaseLegend>
						<Typography
							variant="body1"
							fontFamily={"Inter"}
							fontWeight={400}
							fontSize={"14px"}
							lineHeight={"normal"}
							color="#636364"
							textAlign={"center"}
							whiteSpace={"normal"}
						>
							Your Password has expired. Please enter a new one.
						</Typography>
					</Box>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">New Password</BaseLabel>
						<BaseInput
							required
							type="password"
							name="newPassword"
							value={formDetails.newPassword}
							placeholder="New Password"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Confirm Password</BaseLabel>
						<BaseInput
							required
							type="password"
							name="confirmPassword"
							value={formDetails.confirmPassword}
							placeholder="Confirm Password"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"#FF0000"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box component={"div"} className="call-to-action">
						<BaseButton
							type="submit"
							variant="contained"
							sx={{
								width: "100%",
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Reset Password
								</Typography>
							)}
						</BaseButton>
					</Box>
				</PasswordResetWrapper>
			)}
		</AuthLayout>
	);
};
